import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { chequesApi, type ChequeLayout, type ChequePositions } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad, errText } from "../ui/useLoad";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { rupeesInWords } from "../lib/india";
import { toUrl } from "../shell/routes";
import { LAYOUTS } from "./Cheques";

// Printing a cheque (sql/263_a) on a CTS-2010 leaf: 202 × 92 mm. Positions are in millimetres from the leaf's top-left;
// each bank's preset starts from the CTS-2010 standard and can be nudged after a test print (saved on the cheque book).
type Field = "date" | "payee" | "words" | "amount" | "crossing";
const CTS: Record<Field, { x: number; y: number }> = { date: { x: 157, y: 8 }, payee: { x: 20, y: 20 }, words: { x: 30, y: 29 }, amount: { x: 158, y: 36 }, crossing: { x: 6, y: 6 } };
const SHIFT: Record<ChequeLayout, Partial<Record<Field, { x: number; y: number }>>> = {
  cts: {}, custom: {},
  sbi: { date: { x: 156, y: 7 }, words: { x: 28, y: 28 } },
  hdfc: { date: { x: 158, y: 9 }, payee: { x: 22, y: 21 } },
  icici: { date: { x: 157, y: 9 }, amount: { x: 160, y: 37 } },
  axis: { payee: { x: 18, y: 21 }, words: { x: 27, y: 30 } },
  kotak: { date: { x: 155, y: 8 }, amount: { x: 157, y: 35 } },
};
const FIELDS: [Field, string][] = [["date", "Date"], ["payee", "Payee"], ["words", "Amount in words"], ["amount", "Amount in figures"], ["crossing", "A/c payee crossing"]];
const ddmmyyyy = (d: string) => `${d.slice(8, 10)}${d.slice(5, 7)}${d.slice(0, 4)}`;
const figures = (n: number) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function ChequePrint() {
  const { id = "" } = useParams();
  const { company, ref } = useCompany();
  const d = useLoad(() => chequesApi.print(company.id, id), [company.id, id]);
  const [pos, setPos] = useState<Record<Field, { x: number; y: number }>>(CTS);
  const [msg, setMsg] = useState<string | null>(null); const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!d.data) return;
    const base = { ...CTS, ...SHIFT[d.data.layout] };
    setPos({ ...base, ...(d.data.positions as Record<Field, { x: number; y: number }>) });
  }, [d.data]);
  if (!d.data) return <section className="page"><PageHead crumb="Banking" title="Print cheque" />{d.error ? <ErrorNote text={d.error.message} /> : <Loading />}</section>;
  const c = d.data;
  const words = rupeesInWords(c.amount);
  const at = (f: Field) => ({ left: `${pos[f].x}mm`, top: `${pos[f].y}mm` });
  const nudge = (f: Field, k: "x" | "y", v: string) => { const n = Number(v); if (Number.isFinite(n) && n >= 0 && n <= 202) setPos({ ...pos, [f]: { ...pos[f], [k]: n } }); };
  const savePositions = () => {
    if (!c.book_id) { setErr("This leaf isn't from a cheque book, so its positions can't be saved: add the cheque book under Banking › Cheques."); return; }
    chequesApi.books(company.id).then((bs) => {
      const b = bs.find((x) => x.id === c.book_id);
      if (!b) throw new Error("Cheque book not found");
      return chequesApi.saveBook(company.id, { id: b.id, ledger_id: b.ledger_id, from_no: String(b.from_no), to_no: String(b.to_no), layout: b.layout, positions: pos as ChequePositions });
    }).then(() => { setErr(null); setMsg("Positions saved for this cheque book."); }).catch((e) => setErr(errText(e)));
  };
  return (
    <section className="page cheque-page">
      <PageHead crumb="Banking" title={`Cheque ${c.cheque_no}`}>
        <button className="primary" onClick={() => window.print()}>Print</button>
      </PageHead>
      <p className="muted small no-print">{c.account} · {c.bank} · {LAYOUTS.find(([k]) => k === c.layout)?.[1]} layout. Put the leaf in the printer's manual feed, face up, top edge first.
        {c.pdc && " This is a post-dated cheque: it carries the later date."} <Link to={toUrl("banking/cheques")}>Back to cheques</Link></p>
      {/* the leaf's own page size, only while this page is open (other prints stay A4) */}
      <style>{"@media print { @page { size: 202mm 92mm; margin: 0; } }"}</style>
      <div className="cheque-leaf" aria-label="Cheque">
        {c.ac_payee && <div className="cheque-crossing" style={at("crossing")}>A/C PAYEE</div>}
        <div className="cheque-date mono" style={at("date")}>{ddmmyyyy(c.date).split("").map((ch, i) => <span key={i}>{ch}</span>)}</div>
        <div className="cheque-payee" style={at("payee")}>{c.payee ?? ""}</div>
        <div className="cheque-words" style={at("words")}>{words}</div>
        <div className="cheque-amount mono" style={at("amount")}>{figures(c.amount)}/-</div>
      </div>
      {ref?.can_edit && <details className="no-print cheque-adjust"><summary>Adjust the positions (mm from the leaf's top-left)</summary>
        <table className="grid-table compact"><tbody>{FIELDS.map(([f, l]) => <tr key={f}><td>{l}</td>
          <td><label className="inline-label">Across<input className="num short" inputMode="decimal" value={pos[f].x} onChange={(e) => nudge(f, "x", e.target.value)} /></label></td>
          <td><label className="inline-label">Down<input className="num short" inputMode="decimal" value={pos[f].y} onChange={(e) => nudge(f, "y", e.target.value)} /></label></td></tr>)}</tbody></table>
        <button onClick={savePositions}>Save for this cheque book</button>
      </details>}
      <ErrorNote text={err} />{msg && <p className="msg ok no-print">{msg}</p>}
    </section>
  );
}
